import { useQuery } from '@apollo/client';
import CustomBreadcrumb from '@website/components/CustomBreadcrumb';
import { Store } from '@website/Store';
import {
  UserBookingDetailsDocument,
  UserBookingDetailsQuery,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import Link from 'next/link';
import { useContext } from 'react';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { AiOutlineMinus } from 'react-icons/ai';
import { IoMdCheckmarkCircleOutline } from 'react-icons/io';
import { Image } from '../../media/utils/imgix';
import ErrorPage from '../../utilities/ErrorPage';
import LoadingPage from '../../utilities/LoadingPage';
import BookingBalanceOutstandingAlert from '../userID/BookingBalanceOutstandingAlert';
import BookingOwnerTripFormAlert from '../userID/BookingOwnerTripFormAlert';
import BookingInvoice from './sections/BookingInvoice';
import FAQs from './sections/FAQs';
import Guests from './sections/Guests';
import Host from './sections/Host';
import Itinerary from './sections/Itinerary';
import MeetPoints from './sections/MeetPoints';
import TripPhotos from './sections/TripPhotos';

/**
 * Check if the booking is active
 *
 * A booking is active if there is at least one space that is not cancelled.
 */
export function checkBookingActive(
  spaces: Pick<UserBookingDetailsQuery['booking']['spaces'][0], 'active'>[],
): boolean {
  return !!spaces?.some((space) => space.active === true);
}

/**
 * Count Active Spaces
 */
export function countActiveSpaces(
  booking: UserBookingDetailsQuery['booking'],
): number {
  const activeSpaces = booking?.spaces?.filter(
    (space) => space.active === true,
  );

  return activeSpaces.length;
}

/**
 * User Booking Details Page Content
 */
export default function UserBookingDetails({ query }) {
  const { state } = useContext(Store);
  const { bookingId, userID } = query;

  const { data, loading, error } = useQuery(UserBookingDetailsDocument, {
    variables: { id: bookingId },
    fetchPolicy: 'cache-and-network',
  });

  const booking = data?.booking;
  const group = booking?.group;
  const trip = booking?.trip;

  // Handle loading/errors
  if (loading && !data) {
    return <LoadingPage />;
  }

  if (error && !data) {
    return <ErrorPage error={error} />;
  }

  // Create isInFuture boolean
  const isInFuture =
    DateTime.now() <
    DateTime.fromISO(group.start.utc, {
      zone: 'utc',
    });

  // Check if booking is active or totally cancelled and set variable
  const isBookingActive = checkBookingActive(booking.spaces);

  return (
    <Container className="">
      <CustomBreadcrumb
        items={[
          { text: 'Home', href: '/' },
          { text: 'Dashboard', href: `/me/${booking.user.id}` },
          {
            text: `Booking`,
            active: true,
          },
        ]}
      />

      <BookingBalanceOutstandingAlert bookings={[booking]} />
      <BookingOwnerTripFormAlert bookings={[booking]} />
      <Row>
        <Col md={7} className="mb-3">
          <div className="d-md-none mt-3">
            <Image
              imgKey={booking.trip.image.file.key}
              imgixParams={{
                auto: `compress,format,enhance,redeye`,
                crop: 'faces',
                fit: 'crop',
                ar: `1.5:1`,
                saturation: isBookingActive ? '0' : '-100',
              }}
              className="rounded w-100 shadow"
              sizes="100vw"
            />
          </div>
          <div className="text-block">
            <p className="subtitle">
              {DateTime.fromISO(booking.group.start.utc)
                .setZone(group.start.zone)
                .toLocaleString(DateTime.DATE_HUGE)}{' '}
              -{' '}
              {DateTime.fromISO(booking.group.end.utc)
                .setZone(group.end.zone)
                .toLocaleString(DateTime.DATE_HUGE)}
            </p>
            <h1 className="hero-heading mb-3">{trip.title}</h1>
          </div>

          <MeetPoints
            spaces={booking.spaces}
            userID={userID}
            booking={booking}
          />

          <Itinerary booking={booking} />
          <Guests
            group={booking.group}
            isInFuture={isInFuture}
            spaces={booking.spaces}
            userID={booking.user.id}
          />

          <TripPhotos group={booking.group} media={group.media.items} />

          <Host booking={booking} userID={booking.user.id} />
          {(booking.user.id === state.user.id ||
            state.user.roles.includes('admin')) && (
            <BookingInvoice
              booking={booking}
              isInFuture={isInFuture}
              isBookingActive={isBookingActive}
            />
          )}

          {((trip.includes && trip.includes?.length > 0) ||
            (trip.excludes && trip.excludes?.length > 0)) && (
            <div className="text-block">
              <div className="mb-3">
                {trip.includes?.length > 0 && (
                  <Col xs={12}>
                    <h5>Price Includes</h5>
                    <ul className="list-unstyled">
                      {trip.includes.map((i) => (
                        <li key={i}>
                          <IoMdCheckmarkCircleOutline
                            className="me-1 text-success"
                            size={20}
                          />
                          {i}
                        </li>
                      ))}
                    </ul>
                  </Col>
                )}

                {trip.excludes?.length > 0 && (
                  <Col xs={12}>
                    <h5>Not Included</h5>
                    <ul className="list-unstyled">
                      {trip.excludes.map((i) => (
                        <li key={i}>
                          <AiOutlineMinus className="me-1 text-danger" />
                          {i}
                        </li>
                      ))}
                    </ul>
                  </Col>
                )}
              </div>
            </div>
          )}

          <div className="text-block">
            <div className="mb-3">
              <div>
                <h5>Insurance</h5>

                <div className="pt-2">
                  We highly recommend getting travel insurance that covers you
                  for the whole duration of your trip. We've teamed up with{' '}
                  <a
                    href="https://riseandshield.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Rise & Shield
                  </a>{' '}
                  who offer great cover for adventure travel activities.
                </div>

                <div className="pt-2">
                  <Button
                    href="https://riseandshield.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Get Insurance Quote
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="text-block">
            <div className="mb-3">
              <div>
                <h5>Exclusive discounts</h5>

                <div className="pt-2">
                  As a valued Skyhook customer, you can enjoy special discounts
                  we've secured with our gear and travel partners. Check out
                  your{' '}
                  <Link href={`/me/${booking.user.id}/discounts`}>
                    exclusive coupon offers.
                  </Link>{' '}
                </div>
              </div>
            </div>
          </div>

          <FAQs booking={booking} />
        </Col>

        <Col md={5} className="ps-xl-5 d-none d-md-block h-100 ">
          <Link href={`/trips/${booking.trip.urlSlug}`} passHref>
            <Card className="border-0 shadow hover-animate">
              <Image
                imgKey={trip.image.file.key}
                imgixParams={{
                  auto: 'compress,format,enhance,redeye',
                  crop: 'faces',
                  fit: 'crop',
                  ar: `1.5:1`,
                  saturation: isBookingActive ? '0' : '-100',
                }}
                className="w-100 h-100 rounded"
                sizes="(min-width: 1400px) 490px, (min-width: 1200px) 415px, (min-width: 992px) 376px, (min-width: 768px) 276px, 100vw"
              />
            </Card>
          </Link>
        </Col>
      </Row>
    </Container>
  );
}
